/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-get, ember/no-mixins, ember/no-runloop, no-unused-vars, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { debounce } from '@ember/runloop';
import ProgressionType from '@linc-technologies/linced-common/enums/progression-type';
import StudentReportState from '@linc-technologies/linced-common/enums/reports/student-report-state';
import {
  COMPONENT_FIELD_MAP,
  manualEditProperties,
  saveSection,
  queueSectionSave,
} from 'linced-app/utils/reporting';
import ReportingWriteMixin from 'linced-app/mixins/reporting-write';
import ReportingBaseMixin from 'linced-app/mixins/reporting-base';

export default class WriteController extends Controller.extend(
  ReportingWriteMixin,
  ReportingBaseMixin,
) {
  @service
  personOptions;

  @service
  router;

  @service
  store;

  @service
  studentReportActions;

  @service
  toaster;

  @computed('parentController.editorOptions')
  get editorOptions() {
    return this.parentController.get('editorOptions');
  }

  @computed('parentController.progressChartColors')
  get progressChartColors() {
    return this.parentController.get('progressChartColors');
  }

  @computed('studentReportSectionMap', 'person.id', 'activeSection.id')
  get _activeSectionRecord() {
    let personId = this.get('person.id');
    let sectionId = this.get('activeSection.id');
    if (!personId || !sectionId) return null;
    let map = this.studentReportSectionMap || {};
    return (map[personId] || {})[sectionId] || null;
  }

  @computed('_activeSectionRecord.complete')
  get activeSectionFullyComplete() {
    let record = this._activeSectionRecord;
    return !!(record && record.get('complete'));
  }

  @computed('activeReportContentSections.[]', 'activeSection.id')
  get nextSection() {
    let sections = this.activeReportContentSections || [];
    let currentId = this.get('activeSection.id');
    let idx = sections.findIndex((s) => s.get('id') === currentId);
    return idx >= 0 ? sections[idx + 1] || null : null;
  }

  // Single-student state
  @tracked
  person = null;

  @tracked
  studentReport = null;

  // Evidence sidepeek
  @tracked
  evidenceSidepeekOpen = false;

  @tracked
  addGoalsItems;

  @tracked
  addGoalsSelected;

  @tracked addGoalsStudent;

  @tracked addGoalsSectionLabel;

  @tracked addGoalsSubject;

  @tracked addGoalsCategory;

  @action
  setAddGoalsCategory(value) {
    this.addGoalsCategory = value;
  }

  @tracked addGoalsOpen;

  @tracked activeReport;

  @tracked activeReportContentSections;

  @tracked activeSection;

  @tracked writeReportsOpen;

  @tracked studentReportMap;

  @tracked otjProgressions;

  @tracked showRubricDialog;

  initSingleStudentWriteView(report, contentSections, person, studentReport) {
    this.person = person;
    this.studentReport = studentReport;
    this.activeReport = report;
    this.activeReportContentSections = contentSections;
    this.activeSection = contentSections[0];
    this.writeReportsOpen = true;

    if (!studentReport) return;

    let sectionMap = {};
    let reportMap = {};
    let studentId = person.get('id');
    reportMap[studentId] = studentReport;

    let allSections = this.store.peekAll('reports/student-report-section');
    let reportSections = allSections.filter(
      (s) => s.belongsTo('studentReportId').id() === studentReport.get('id'),
    );
    sectionMap[studentId] = {};
    reportSections.forEach((section) => {
      let templateSectionId = section.belongsTo('reportTemplateSectionId').id();
      sectionMap[studentId][templateSectionId] = section;
    });

    this.set('studentReportSectionMap', sectionMap);
    this.studentReportMap = reportMap;

    this._registerBeforeUnloadSave();
    this._loadStudentPosts([person]);
    this._loadTriangulationData([person]);
  }

  cleanupWriteView() {
    this._deregisterBeforeUnloadSave();
    this.setProperties({
      person: null,
      studentReport: null,
      writeReportsOpen: false,
      activeReport: null,
      activeSection: null,
      activeReportContentSections: null,
      studentReportSectionMap: null,
      studentReportMap: null,
      postCountMap: null,
      studentPostsMap: null,
      evidenceSidepeekOpen: false,
      showRubricDialog: false,
      addGoalsOpen: false,
      addGoalsStudent: null,
      addGoalsSectionLabel: null,
      addGoalsSubject: null,
      addGoalsCategory: null,
      addGoalsItems: null,
      addGoalsSelected: null,
    });
    this._clearTriangulationData();
  }

  @action
  async exitWrite() {
    // Await pending saves before leaving; the group hub refetches counts on
    // arrival and would otherwise read pre-edit state.
    await this.flushPendingSectionSaves();
    if (this.isDestroyed || this.isDestroying) return;
    this.cleanupWriteView();
    this.router.transitionTo('reporting.group.student');
  }

  @action
  selectSection(section) {
    this.activeSection = section;
    if (this.writeReportsOpen) {
      this._loadStudentPosts([this.person]);
      this._loadTriangulationData([this.person]);
    }
  }

  @action
  selectSectionByValue(sectionId) {
    let section = (this.activeReportContentSections || []).find(
      (s) => s.get('id') === sectionId,
    );
    if (section) {
      this.selectSection(section);
    }
  }

  @action
  updateSectionField(componentType, value) {
    this._updateWriteSectionField(this.person, componentType, value);
  }

  @action
  autoPopulateSection(populateData) {
    this._autoPopulateWriteSection(this.person, populateData);
  }

  @action
  regenerateSectionField(componentType) {
    this._regenerateWriteSectionField(this.person, componentType);
  }

  @action
  async markSectionComplete() {
    let student = this.person;
    let section = this.getStudentReportSection(
      student.get('id'),
      this.activeSection.get('id'),
    );
    if (!section) return;
    section.set('complete', true);
    await queueSectionSave(section);

    let studentSections = this.studentReportSectionMap[student.get('id')];
    if (!studentSections) return;
    let sectionIds = this.activeReportContentSections.map((s) => s.get('id'));
    let allComplete = sectionIds.every((id) => {
      let s = studentSections[id];
      return s && s.get('complete');
    });

    if (allComplete) {
      let studentReport = this.studentReport;
      if (
        studentReport &&
        studentReport.get('state') === StudentReportState.DRAFT
      ) {
        try {
          await this.studentReportActions.submit(studentReport);
          this.toaster.success('Report submitted for review.');
          this.router.transitionTo(
            'reporting.group.student.review',
            studentReport.get('id'),
          );
        } catch (err) {
          this.toaster.error('Failed to submit report.');
        }
      }
    }
  }

  @action
  async editSection() {
    let student = this.person;
    let section = this.getStudentReportSection(
      student.get('id'),
      this.activeSection.get('id'),
    );
    if (!section) return;
    section.set('complete', false);
    await queueSectionSave(section);

    let studentReport = this.studentReport;
    if (
      studentReport &&
      studentReport.get('state') === StudentReportState.READY_TO_REVIEW
    ) {
      try {
        await this.studentReportActions.reopen(studentReport);
      } catch (e) {
        this.toaster.error('Failed to edit section.');
      }
    }
  }

  @action
  updateJudgement(elementId, value) {
    this._updateWriteJudgement(this.person, elementId, value);
  }

  @action
  updateEffort(elementId, value) {
    this._updateWriteEffort(this.person, elementId, value);
  }

  @action
  onOtjJudgementChange(judgementValue) {
    let student = this.person;
    let section = this.getStudentReportSection(
      student.get('id'),
      this.activeSection.get('id'),
    );
    if (!section) return;
    section.set('judgement', String(judgementValue));
    if (section.get('complete')) {
      section.set('complete', false);
    }
    saveSection(section, this.toaster);
  }

  @action
  otjChartData(records) {
    return this._otjChartData(records);
  }

  @action
  async updateOtjRecords() {
    let subject = this.otjSubject;
    if (!subject) return;
    let subjectId = subject.get('id');
    let progressions = await this.store.query('milestones/progression', {
      goalSubjectId: subjectId,
      type: ProgressionType.PROGRESSION,
      latest: true,
    });

    let student = this.person;
    let progressionMap = {};
    progressionMap[student.get('id')] = [];
    progressions.forEach((p) => {
      let personId =
        p.person_id ||
        (p.belongsTo &&
          p.belongsTo('personId') &&
          p.belongsTo('personId').id());
      if (personId && progressionMap[personId]) {
        progressionMap[personId].push(p);
      }
    });

    this.otjProgressions = progressionMap;
  }

  @action
  openEvidenceSidepeek() {
    this.evidenceSidepeekOpen = true;
  }

  @action
  closeEvidenceSidepeek() {
    this.evidenceSidepeekOpen = false;
  }

  @action
  addPostToNarrative(post) {
    let student = this.person;
    let section = this.getStudentReportSection(
      student.get('id'),
      this.activeSection.get('id'),
    );
    if (!section) return;

    let narrativeComponent = (this.activeSectionTextComponents || []).find(
      (c) => COMPONENT_FIELD_MAP[c.type] === 'narrative',
    );
    if (!narrativeComponent) return;
    let field = COMPONENT_FIELD_MAP[narrativeComponent.type];
    if (!field) return;

    let postText = (post.get('content') || post.get('subject') || '').trim();
    if (!postText) return;

    let existing = (section.get(field) || '').trim();
    // Adding a post counts as a manual edit, so sliders won't replace it
    section.setProperties(
      manualEditProperties(
        field,
        existing ? `${existing}\n${postText}` : postText,
      ),
    );
    if (section.get('complete')) {
      section.set('complete', false);
    }
    this.notifyPropertyChange('studentReportSectionMap');
    debounce(section, saveSection, 1000);
  }

  @action
  openRubricDialog() {
    this.showRubricDialog = true;
  }

  @action
  closeRubricDialog() {
    this.showRubricDialog = false;
  }

  @action
  openAddGoals(student, sectionLabel) {
    this.addGoalsStudent = student;
    this.addGoalsSectionLabel = sectionLabel || null;
    this.addGoalsSubject = null;
    this.addGoalsCategory = null;
    this.addGoalsItems = null;
    this.addGoalsSelected = [];
    this.addGoalsOpen = true;
  }

  @action
  closeAddGoals() {
    this.addGoalsOpen = false;
    this.addGoalsStudent = null;
    this.addGoalsSectionLabel = null;
    this.addGoalsSubject = null;
    this.addGoalsCategory = null;
    this.addGoalsItems = null;
    this.addGoalsSelected = null;
  }

  @action
  async selectAddGoalsSubject(subject) {
    this.addGoalsSubject = subject;
    this.addGoalsCategory = null;
    this.addGoalsSelected = [];
    try {
      let goals = await this.store.query('goals/goal', {
        subjectId: subject.get('id'),
      });
      this.addGoalsItems = goals;
    } catch (e) {
      this.addGoalsItems = [];
    }
  }

  @action
  filterAddGoalsByCategory(category) {
    if (
      this.addGoalsCategory &&
      this.addGoalsCategory.get('id') === category.get('id')
    ) {
      this.addGoalsCategory = null;
    } else {
      this.addGoalsCategory = category;
    }
  }

  @action
  toggleAddGoal(goal) {
    let selected = this.addGoalsSelected;
    if (selected.includes(goal)) {
      this.addGoalsSelected = selected.filter((g) => g !== goal);
    } else {
      this.addGoalsSelected = [...selected, goal];
    }
  }

  @action
  confirmAddGoals() {
    let student = this.person;
    if (!student) return;
    let sectionId = this.get('activeSection.id');
    let section = this.getStudentReportSection(student.get('id'), sectionId);
    let changed = this.studentReportActions.confirmAddGoals(
      section,
      this.addGoalsSelected,
    );
    if (changed) {
      this.notifyPropertyChange('studentReportSectionMap');
    }
    this.addGoalsOpen = false;
    this.addGoalsStudent = null;
    this.addGoalsSectionLabel = null;
    this.addGoalsSubject = null;
    this.addGoalsCategory = null;
    this.addGoalsItems = null;
    this.addGoalsSelected = null;
  }
}
